// contexts/OrganizationContext.jsx
import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import OrganizationService from '../services/organization.service';
import AccountService from '../services/account.service';
import ProjectService from '../services/project.service';
import { useAuth } from '../useAuth';
import { readDefaultContext } from '../lib/defaultContext';

// Cuanto se espera a que los proyectos den una respuesta antes de darlos por
// resueltos, para que un fallo de confirmacion de cuenta no deje un spinner fijo.
const PROJECTS_SETTLE_TIMEOUT_MS = 5000;

/**
 * Organization Context
 * Maneja el estado global de organizaciones, cuentas y proyectos
 */
const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

export const OrganizationProvider = ({ children }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // Estado de organizaciones
  const [organizations, setOrganizations] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  // Whether loadOrganizations has completed at least once. Used to gate the
  // org-context restore: falling back to personal is only valid once we've
  // actually fetched the org list — not while it's still the initial empty array.
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);
  
  // Estado de cuentas
  const [personalAccount, setPersonalAccount] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(() => {
    // Intentar cargar cuenta persistida al inicio
    try {
      const savedAccountId = localStorage.getItem('currentAccountId');
      const savedAccountType = localStorage.getItem('currentAccountType');
      if (savedAccountId && savedAccountType) {
        const meta = JSON.parse(localStorage.getItem('currentAccountMeta') || '{}');
        return {
          id: savedAccountId,
          type: savedAccountType,
          organization_id: meta.organization_id || null,
          name: meta.name || null,
          _restored: true,
        };
      }
    } catch {
      // Ignore error
    }
    return null;
  });
  const [accountsLoading, setAccountsLoading] = useState(false);
  
  // Estado de proyectos
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  // `projectsLoading` solo dice "no estoy pidiendo ahora mismo", no "ya tengo
  // respuesta". Tras una recarga la cuenta llega marcada `_restored` y
  // loadProjects todavia no corrio, asi que projects=[] y loading=false a la vez
  // — y las vistas leian eso como "esta cuenta no tiene proyectos" y pintaban
  // el estado vacio encima de un deep-link valido.
  //
  // `projectsSettled` distingue las dos cosas: false hasta que una carga de esta
  // cuenta termina. Va con tope, porque si la cuenta nunca se confirma la carga
  // no arranca y quedaria un "cargando" perpetuo.
  const [projectsSettled, setProjectsSettled] = useState(false);
  
  // Contexto actual (PERSONAL o ORGANIZATION)
  const [currentContext, setCurrentContext] = useState(() => {
    // Intentar cargar desde localStorage al inicio
    try {
      const saved = localStorage.getItem('currentContext');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  
  // Errores
  //
  // El error del contexto tiene dos productores con formas distintas: los
  // services que siguen en auth_service devuelven `error: string[]`, y los ya
  // migrados a su servicio propio devuelven un `message` canónico (string).
  // Los consumidores lo renderizan como lista (`error.join(', ')` en
  // OrganizationsView), así que un string crudo tumbaba la vista entera con
  // "error.join is not a function". Se normaliza acá, en el seam, en vez de
  // parchear cada consumidor.
  const [error, setErrorState] = useState(null);
  const setError = useCallback((value) => {
    if (value === null || value === undefined) {
      setErrorState(null);
      return;
    }
    setErrorState(Array.isArray(value) ? value : [String(value)]);
  }, []);

  // Ref para leer el contexto actual en callbacks async (evita closures obsoletos)
  const currentContextRef = useRef(currentContext);
  useEffect(() => {
    currentContextRef.current = currentContext;
  }, [currentContext]);

  // Same, for the account — loadPersonalAccount needs to see the account that was
  // restored from localStorage to avoid flipping to personal on a hard reload.
  const currentAccountRef = useRef(currentAccount);
  useEffect(() => {
    currentAccountRef.current = currentAccount;
  }, [currentAccount]);

  /**
   * Persistir contexto actual en localStorage
   */
  const persistCurrentContext = (context) => {
    try {
      if (context) {
        localStorage.setItem('currentContext', JSON.stringify(context));
      } else {
        localStorage.removeItem('currentContext');
      }
    } catch (error) {
      console.warn('Failed to persist context to localStorage:', error);
    }
  };

  /**
   * Persistir la cuenta actual para restauración
   */
  const persistCurrentAccount = (account) => {
    try {
      if (account && account.id) {
        localStorage.setItem('currentAccountId', account.id);
        localStorage.setItem('currentAccountType', account.type || 'PERSONAL');
        // Meta is what lets a hard reload rebuild an ORGANIZATION context (the
        // org id, needed to re-fetch it) instead of dropping to personal.
        localStorage.setItem('currentAccountMeta', JSON.stringify({
          organization_id: account.organization_id || null,
          name: account.name || null,
        }));
      } else {
        localStorage.removeItem('currentAccountId');
        localStorage.removeItem('currentAccountType');
        localStorage.removeItem('currentAccountMeta');
      }
    } catch (error) {
      console.warn('Failed to persist current account to localStorage:', error);
    }
  };

  /**
   * Cargar cuenta personal del usuario
   */
  const loadPersonalAccount = async () => {
    if (!isAuthenticated || !user) return;
    setAccountsLoading(true);
    const result = await AccountService.getPersonalAccount();
    if (result.success) {
      setPersonalAccount(result.data);

      // Leer el contexto/cuenta actuales al momento de completarse (no el closure inicial)
      const latestContext = currentContextRef.current;
      const restoredAccount = currentAccountRef.current;
      if (!latestContext) {
        // No context in memory. If a hard reload restored an ORGANIZATION account,
        // rebuild its context instead of dropping to personal — otherwise the
        // account silently changes and the URL's project (of the org) 403s.
        if (restoredAccount?.type === 'ORGANIZATION' && restoredAccount?.organization_id) {
          const orgContext = {
            type: 'ORGANIZATION',
            id: restoredAccount.organization_id,
            name: restoredAccount.name || null,
          };
          setCurrentContext(orgContext);
          persistCurrentContext(orgContext);
          // Leave the restored org account in place; the restore-org effect hydrates it.
        } else {
          const defaultContext = {
            type: 'PERSONAL',
            id: result.data.id,
            name: 'Personal'
          };
          setCurrentContext(defaultContext);
          setCurrentAccount(result.data);
          persistCurrentContext(defaultContext);
          persistCurrentAccount(result.data);
        }
      } else if (latestContext.type === 'PERSONAL') {
        // Solo asignar cuenta personal si el contexto sigue siendo personal
        setCurrentAccount(result.data);
        persistCurrentAccount(result.data);
      }
      // Si es ORGANIZATION, no tocar currentAccount — changeContext ya lo manejó
    } else {
      setError(result.error);
    }
    setAccountsLoading(false);
  };

  /**
   * Cargar organizaciones del usuario
   */
  const loadOrganizations = async () => {
    if (!isAuthenticated) return;
    setOrganizationsLoading(true);
    const result = await OrganizationService.listOrganizations();
    if (result.success) {
      setOrganizations(Array.isArray(result.data) ? result.data : []);
    } else {
      setError(result.error);
      setOrganizations([]);
    }
    setOrganizationsLoading(false);
    setOrganizationsLoaded(true);
  };

  /**
   * Cargar proyectos según el contexto actual
   */
  const loadProjects = async () => {
    if (!isAuthenticated || !currentAccount) return;
    
    // Loading projects
    setProjectsLoading(true);
    
    // Clear projects immediately to avoid showing wrong projects
    setProjects([]);
    
    let result;
    if (currentContext?.type === 'PERSONAL') {
      // Para cuenta personal, usar el account_id de la cuenta personal
      result = await ProjectService.listPersonalProjects({ accountId: currentAccount.id });
    } else if (currentContext?.type === 'ORGANIZATION') {
      // Para organización, usar el organization_id (no el organizational_account_id)
      const organizationId = currentAccount.organization_id || currentContext.id;
      console.log('[OrgCtx] loadProjects ORG', { organizationId, currentAccount, currentContext });
      result = await ProjectService.listOrganizationProjects(organizationId);
    }
    
    if (result?.success) {
      // Projects loaded
      setProjects(result.data);
    } else {
      console.error('❌ Failed to load projects:', result?.error);
      setError(result?.error);
      setProjects([]);
    }
    setProjectsLoading(false);
    setProjectsSettled(true);
  };

  /**
   * Crear nueva organización
   */
  const createOrganization = async (organizationData) => {
    setOrganizationsLoading(true);
    const result = await OrganizationService.createOrganization(organizationData);
    
    if (result.success) {
      // Recargar organizaciones
      await loadOrganizations();
    } else {
      setError(result.error);
    }
    setOrganizationsLoading(false);
    return result;
  };

  /**
   * Actualizar organización
   */
  const updateOrganization = async (organizationId, updates) => {
    const result = await OrganizationService.updateOrganization(organizationId, updates);
    
    if (result.success) {
      // Actualizar en el estado local
      setOrganizations(orgs => 
        orgs.map(org => 
          org.id === organizationId ? { ...org, ...updates } : org
        )
      );
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Eliminar organización
   */
  const deleteOrganization = async (organizationId) => {
    const result = await OrganizationService.deleteOrganization(organizationId);
    
    if (result.success) {
      // Remover del estado local
      setOrganizations(orgs => orgs.filter(org => org.id !== organizationId));
      
      // Si era el contexto actual, cambiar a personal
      if (currentContext?.type === 'ORGANIZATION' && currentContext?.id === organizationId) {
        changeContext({ type: 'PERSONAL', id: personalAccount.id });
      }
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Cambiar contexto actual (Personal <-> Organization)
   * Cuando se selecciona una organización, se recarga la página virtualmente:
   * - Se obtiene la información completa de la organización
   * - Se cambia el account ID activo
   * - Se recargan todos los recursos (assistants, avatars, projects, etc.)
   */
  const changeContext = async (newContext) => {
    setError(null);
    
    try {
      if (newContext.type === 'PERSONAL') {
        setCurrentContext(newContext);
        setCurrentAccount(personalAccount);
        persistCurrentContext(newContext);
        persistCurrentAccount(personalAccount);
      } else if (newContext.type === 'ORGANIZATION') {

        // 1. Obtener información completa de la organización
        const orgResult = await OrganizationService.getOrganization(newContext.id);
        if (!orgResult.success) {
          setError(orgResult.error);
          return;
        }

        // 2. Validar que exista el organizational_account_id ANTES de tocar el estado
        // Si se actualiza currentContext sin actualizar currentAccount, la UI mostraría
        // el nombre de la org pero las conexiones se crearían en la cuenta personal.
        const organizationalAccountId = orgResult.data.organizational_account_id;
        if (!organizationalAccountId) {
          setError(['No organizational account ID found']);
          return;
        }

        // 3. Actualizar contexto y cuenta de forma atómica (todos los datos están disponibles)
        const updatedContext = {
          ...newContext,
          name: orgResult.data.organization?.name || orgResult.data.organization_name,
          domain: orgResult.data.organization?.domain,
          accountType: 'ORGANIZATION'
        };
        setCurrentContext(updatedContext);
        persistCurrentContext(updatedContext);

        const newAccount = {
          ...orgResult.data.organization,
          id: organizationalAccountId,
          name: orgResult.data.organization?.name || orgResult.data.organization_name,
          type: 'ORGANIZATION',
          organization_id: orgResult.data.organization_id || orgResult.data.organization?.id,
        };

        setCurrentAccount(newAccount);
        persistCurrentAccount(newAccount);
      }
      
    } catch (error) {
      setError(['Failed to switch context: ' + error.message]);
    }
  };

  /**
   * Crear proyecto
   */
  const createProject = async (projectData) => {
    setProjectsLoading(true);
    
    // Preparar los datos del proyecto según el contexto
    const payload = { ...projectData };
    
    if (currentContext?.type === 'ORGANIZATION') {
      // Para organizaciones, solo enviar organization_id
      // El backend acepta exactamente uno de account_id u organization_id
      payload.organization_id = currentAccount?.organization_id || currentContext.id;
      delete payload.account_id;
    } else if (currentContext?.type === 'PERSONAL') {
      // Para proyectos personales, usar account_id personal
      payload.account_id = currentAccount?.id;
      // Remover organization_id para proyectos personales
      delete payload.organization_id;
    } else {
      setError(['Invalid context for project creation']);
      setProjectsLoading(false);
      return { success: false, error: ['Invalid context'] };
    }
    
    const result = await ProjectService.createProject(payload);
    
    if (result.success) {
      // Refresh from API to get authoritative data (created_at, etc.)
      await loadProjects();
    } else {
      setError(result.error);
    }
    setProjectsLoading(false);
    return result;
  };

  /**
   * Actualizar proyecto
   */
  const updateProject = async (projectId, updates) => {
    const result = await ProjectService.updateProject(projectId, updates);
    
    if (result.success) {
      setProjects(projects => 
        projects.map(p => 
          p.id === projectId ? { ...p, ...updates } : p
        )
      );
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Eliminar proyecto
   */
  const deleteProject = async (projectId) => {
    const result = await ProjectService.deleteProject(projectId);
    
    if (result.success) {
      setProjects(projects => projects.filter(p => p.id !== projectId));
    } else {
      setError(result.error);
    }
    return result;
  };

  /**
   * Agregar miembro a organización
   */
  const addMember = async (organizationId, memberData) => {
    const result = await OrganizationService.addMember(organizationId, memberData);
    if (!result.success) {
      setError(result.error);
    }
    return result;
  };

  /**
   * Remover miembro de organización
   */
  const removeMember = async (organizationId, userId) => {
    const result = await OrganizationService.removeMember(organizationId, userId);
    if (!result.success) {
      setError(result.error);
    }
    return result;
  };

  /**
   * Actualizar rol de miembro
   */
  const updateMemberRole = async (organizationId, userId, newRole) => {
    const result = await OrganizationService.updateMemberRole(organizationId, userId, newRole);
    if (!result.success) {
      setError(result.error);
    }
    return result;
  };

  // Cargar datos iniciales cuando el usuario está autenticado
  useEffect(() => {
    if (isAuthenticated && user) {
      loadPersonalAccount();
      loadOrganizations();
    } else if (!authLoading) {
      // Solo limpiar cuando el auth terminó de inicializar y no hay sesión (logout real)
      // Evitar borrar localStorage durante la carga inicial donde isAuthenticated es false momentáneamente
      setCurrentContext(null);
      setCurrentAccount(null);
      setPersonalAccount(null);
      setOrganizations([]);
      setOrganizationsLoaded(false);
      setProjects([]);
      persistCurrentContext(null);
      persistCurrentAccount(null);
    }
  }, [isAuthenticated, user, authLoading]);

  // Restaurar contexto de organización después de cargar datos iniciales
  useEffect(() => {
    if (
      !(
        isAuthenticated &&
        user &&
        personalAccount &&
        currentContext?.type === 'ORGANIZATION' &&
        // Correr si aún no hay cuenta configurada, o si la cuenta es un placeholder
        // restaurado de localStorage (_restored) que todavía no se verificó. Sin
        // esto último, tras un F5 en una org la cuenta queda como _restored para
        // siempre y no cargan proyectos/datos hasta cambiar de org.
        (!currentAccount?.type || currentAccount?._restored)
      )
    ) {
      return;
    }

    const fallbackToPersonal = () => {
      const personalContext = { type: 'PERSONAL', id: personalAccount.id, name: 'Personal' };
      setCurrentContext(personalContext);
      setCurrentAccount(personalAccount);
      persistCurrentContext(personalContext);
      persistCurrentAccount(personalAccount);
    };

    const org = organizations.find(o => o.id === currentContext.id);
    if (org) {
      // Restaurar la organización usando changeContext para obtener la cuenta correcta.
      changeContext(currentContext).catch(error => {
        console.warn('❌ Failed to restore organization context, falling back to personal:', error);
        fallbackToPersonal();
      });
    } else if (organizationsLoaded) {
      // The org list finished loading and the persisted org is genuinely not in it
      // (deleted / access revoked). Only NOW is dropping to personal correct — doing
      // it while the list is still the initial empty array is what kicked an org
      // reload back to personal.
      console.warn('⚠️ Organization not found in loaded organizations, switching to personal');
      fallbackToPersonal();
    }
    // else: orgs not loaded yet → wait for the next render (deps include organizationsLoaded).
  }, [isAuthenticated, user, personalAccount, organizations, organizationsLoaded, currentContext?.id]);

  // Contexto por defecto: en qué cuenta aterrizas cuando no hay ninguna
  // restaurada. Respeta la última usada si hay un currentContext persistido.
  //
  // Antes se elegía `organizations[0]` — el orden de la lista, que el usuario ni
  // ve ni controla. Con ocho contextos en el switcher eso es empezar la sesión
  // desorientado, así que ahora manda la preferencia de /u/settings/preferences
  // y la lista solo decide cuando no se ha elegido nada.
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const restored = localStorage.getItem('currentContext');
    if (restored) return; // respetar la última cuenta usada
    if (currentContext) return; // ya hay contexto en memoria

    const preferred = readDefaultContext();
    if (preferred?.type === 'PERSONAL') return; // elegida a mano: no saltar a una org

    if (organizations && organizations.length > 0) {
      const preferredIsAvailable =
        preferred?.type === 'ORGANIZATION' &&
        organizations.some((o) => o.id === preferred.id);
      changeContext({
        type: 'ORGANIZATION',
        id: preferredIsAvailable ? preferred.id : organizations[0].id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, organizations]);

  // Cargar proyectos cuando cambia la cuenta o cuando el auth termina de inicializar.
  // Skip accounts marked _restored — they come from localStorage and haven't been
  // verified against the current user yet. loadPersonalAccount / changeContext will
  // set a clean account (without _restored) once auth is confirmed.
  useEffect(() => {
    if (isAuthenticated && currentAccount?.id && !currentAccount?._restored) {
      loadProjects();
    }
  }, [currentAccount?.id, currentAccount?._restored, isAuthenticated]);

  // Cambiar de cuenta invalida lo que sabiamos: vuelve a "sin respuesta" hasta
  // que la carga de la cuenta nueva termine.
  useEffect(() => {
    setProjectsSettled(false);
  }, [currentAccount?.id]);

  // Tope: si la cuenta no llega a confirmarse, loadProjects no corre y sin esto
  // el "cargando" no terminaria nunca. Se prefiere mostrar el estado vacio, que
  // al menos es accionable, antes que un spinner que no puede acabar.
  useEffect(() => {
    if (projectsSettled) return undefined;
    const timer = setTimeout(() => setProjectsSettled(true), PROJECTS_SETTLE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [projectsSettled, currentAccount?.id]);

  // Limpiar proyectos cuando no hay cuenta activa
  useEffect(() => {
    if (!currentAccount) {
      setProjects([]);
    }
  }, [currentAccount]);

  const value = {
    // Estado
    organizations,
    organizationsLoading,
    // Si `loadOrganizations` ya terminó al menos una vez. Sin esto, una lista
    // vacía no se distingue de una lista que aún no llegó, y quien lo consuma
    // acaba con un cargando que no puede terminar — el mismo error que tenía
    // `projectsLoading`.
    organizationsLoaded,
    personalAccount,
    currentAccount,
    currentContext,
    projects,
    // Se expone como "todavia no hay respuesta fiable", que es lo que las
    // vistas necesitan para no afirmar "no hay proyectos" antes de tiempo.
    projectsLoading: projectsLoading || (isAuthenticated && !projectsSettled),
    accountsLoading,
    error,
    
    // Acciones de organizaciones
    createOrganization,
    updateOrganization,
    deleteOrganization,
    loadOrganizations,
    // Vuelve a pedir la cuenta personal. Lo consume la sección General de la
    // cuenta para refrescar tras guardar, en vez de recargar la app entera.
    loadPersonalAccount,

    // Acciones de contexto
    changeContext,
    
    // Acciones de proyectos
    createProject,
    updateProject,
    deleteProject,
    loadProjects,
    
    // Acciones de miembros
    addMember,
    removeMember,
    updateMemberRole,
    
    // Utilidades
    setError,
    clearError: () => setError(null)
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export default OrganizationContext;
